/**
 * 📝 [Phase 104 — 2026-07-09] Badge SMS + drawer historique pour la vue admin
 * d'une route de lavage à pression.
 *
 * - Badge cliquable affichant `X envoyés · Y échecs` (couleur selon stats)
 * - Drawer/modal listant chronologiquement les SMS avec :
 *     • statut coloré (✓ envoyé, ✗ échec, ⚠ template/téléphone manquant)
 *     • nom client, template_code, timestamp, éventuelle erreur Twilio
 *
 * Endpoint consommé : GET /api/crm/routes-lavage/{route_id}/sms-history
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { MessageSquare, CheckCircle2, XCircle, AlertTriangle, Loader2, Phone } from "lucide-react";

const API_URL =
  (typeof window !== "undefined" && window.__API_URL__) ||
  process.env.REACT_APP_BACKEND_URL ||
  "";

const authHeader = () => {
  try {
    const t = localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token");
    return t ? { Authorization: `Bearer ${t}` } : {};
  } catch {
    return {};
  }
};

const STATUS_META = {
  envoye: { icon: CheckCircle2, className: "text-emerald-700", label: "Envoyé" },
  erreur: { icon: XCircle, className: "text-red-700", label: "Échec Twilio" },
  template_manquant: { icon: AlertTriangle, className: "text-amber-700", label: "Template manquant" },
  telephone_manquant: { icon: Phone, className: "text-orange-700", label: "Téléphone manquant" },
  phone_invalide: { icon: Phone, className: "text-orange-700", label: "Téléphone invalide" },
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-CA", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function RouteLavageSmsHistoryBadge({ routeId }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ total: 0, envoye: 0, erreur: 0, template_manquant: 0, telephone_manquant: 0 });
  const [sms, setSms] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/crm/routes-lavage/${routeId}/sms-history`,
        { headers: authHeader() }
      );
      setStats(res.data?.stats || {});
      setSms(res.data?.sms || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur au chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (routeId) load();
  }, [routeId]);

  const totalEnvoye = stats.envoye || 0;
  const totalEchecs =
    (stats.erreur || 0) +
    (stats.template_manquant || 0) +
    (stats.telephone_manquant || 0) +
    (stats.phone_invalide || 0);

  const badgeVariant = totalEchecs > 0
    ? "bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200"
    : totalEnvoye > 0
    ? "bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-emerald-200"
    : "bg-slate-100 text-slate-600 border-slate-300 hover:bg-slate-200";

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium transition-colors ${badgeVariant}`}
        data-testid="sms-history-badge"
        title="Voir l'historique SMS de cette route"
      >
        <MessageSquare className="w-3 h-3" />
        {totalEnvoye} envoyé{totalEnvoye > 1 ? "s" : ""} · {totalEchecs} échec{totalEchecs > 1 ? "s" : ""}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-2xl max-h-[85vh] overflow-y-auto"
          data-testid="sms-history-drawer"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-slate-500" />
              Historique des SMS — Route de lavage
            </DialogTitle>
            <DialogDescription>
              Chronologique du plus récent au plus ancien.
            </DialogDescription>
          </DialogHeader>

          {/* Stats agrégées */}
          <div className="flex flex-wrap gap-2 mb-3">
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300" data-testid="sms-stat-envoye">
              ✓ {stats.envoye || 0} envoyé{(stats.envoye || 0) > 1 ? "s" : ""}
            </Badge>
            <Badge className="bg-red-100 text-red-800 border-red-300" data-testid="sms-stat-erreur">
              ✗ {stats.erreur || 0} échec Twilio
            </Badge>
            <Badge className="bg-amber-100 text-amber-800 border-amber-300" data-testid="sms-stat-template">
              ⚠ {stats.template_manquant || 0} template manquant
            </Badge>
            <Badge className="bg-orange-100 text-orange-800 border-orange-300" data-testid="sms-stat-tel">
              ☎ {(stats.telephone_manquant || 0) + (stats.phone_invalide || 0)} tél. manquant/invalide
            </Badge>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            className="mb-3"
            data-testid="sms-history-refresh"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : null}
            Rafraîchir
          </Button>

          {loading ? (
            <div className="text-slate-500 text-sm">Chargement…</div>
          ) : sms.length === 0 ? (
            <div
              className="p-4 border border-dashed border-slate-300 rounded-lg text-center text-sm text-slate-500"
              data-testid="sms-history-empty"
            >
              Aucun SMS envoyé pour cette route pour le moment.
            </div>
          ) : (
            <div className="space-y-2" data-testid="sms-history-list">
              {sms.map((s) => {
                const meta = STATUS_META[s.status] || {
                  icon: MessageSquare,
                  className: "text-slate-600",
                  label: s.status || "?",
                };
                const Icon = meta.icon;
                const clientNom = s?.context?.client_nom || "?";
                const tplCode = s?.context?.template_code || "?";
                return (
                  <div
                    key={s.id}
                    className="p-3 border border-slate-200 rounded-lg bg-white"
                    data-testid={`sms-item-${s.id}`}
                  >
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-2 min-w-0">
                        <Icon className={`w-4 h-4 shrink-0 ${meta.className}`} />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">
                            {clientNom}
                          </p>
                          <p className="text-xs text-slate-500 truncate">
                            {tplCode} · {formatDate(s.sent_at)}
                          </p>
                        </div>
                      </div>
                      <Badge className={`text-[10px] ${meta.className}`}>
                        {meta.label}
                      </Badge>
                    </div>
                    {s.error && (
                      <p className="mt-1 text-xs text-red-600 whitespace-pre-wrap break-all">
                        {s.error}
                      </p>
                    )}
                    {s.body && (
                      <p className="mt-1 text-[11px] text-slate-500 italic line-clamp-2">
                        « {s.body} »
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default RouteLavageSmsHistoryBadge;
